import { useState, useCallback, useMemo } from 'react';
import api from '../../../../utils/functions/api';
import * as actions from '../../../../redux/actions';
import { emptyVehicle, COMPANY_NAME } from '../constants';

export function useVehicle({ employeeId, employees, dispatch }) {
  const [vehicles, setVehicles] = useState([]);
  const [companyVehicles, setCompanyVehicles] = useState([]);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle);
  const [editIdx, setEditIdx] = useState(null);

  const allVehiclesForClaim = useMemo(() => [
    ...companyVehicles,
    ...vehicles.filter((v) => v.owner_type !== 'company'),
  ], [companyVehicles, vehicles]);

  const otherCompanyVehicles = useMemo(() =>
    companyVehicles.filter((cv) => !vehicles.some((v) => v.license_plate === cv.license_plate)),
  [companyVehicles, vehicles]);

  const fetchCompanyVehicles = useCallback(async () => {
    try {
      const { data, status } = await api.get(
        `${process.env.REACT_APP_API_URL}/vehicle-fuel-setting/company-vehicles`,
      );
      if (status === 200) setCompanyVehicles(data || []);
    } catch (e) {
      console.error('[FuelSetting] fetchCompanyVehicles error:', e);
    }
  }, []);

  const saveToBackend = async (newVehicles) => {
    setSaveError('');
    try {
      await dispatch(actions.vehicleFuelSettingUpsert({ employee_id: employeeId, vehicles: newVehicles }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      dispatch(actions.vehicleFuelSettingGet(employeeId));
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || 'บันทึกไม่สำเร็จ';
      setSaveError(msg);
      setTimeout(() => setSaveError(''), 6000);
    }
  };

  const handleSetDefault = (idx) => {
    const next = vehicles.map((v, i) => ({ ...v, is_default: i === idx }));
    setVehicles(next);
    saveToBackend(next);
  };

  const handleDeleteVehicle = (idx) => {
    const next = vehicles.filter((_, i) => i !== idx);
    setVehicles(next);
    saveToBackend(next);
  };

  const openAdd = () => {
    setVehicleForm({ ...emptyVehicle });
    setEditIdx(null);
    setFormOpen(true);
  };

  const openEdit = (idx) => {
    setVehicleForm({ ...vehicles[idx] });
    setEditIdx(idx);
    setFormOpen(true);
  };

  const handleFormSave = () => {
    const data = { ...vehicleForm };
    if (data.owner_type === 'company') {
      data.owner_name = COMPANY_NAME;
      data.owner_employee = null;
      data.owner_reason = '';
    } else if (data.owner_type === 'personal') {
      const emp = employees.find((e) => e._id === (data.owner_employee?._id || data.owner_employee));
      data.owner_name = emp ? `${emp.firstname || ''} ${emp.lastname || ''}`.trim() : '';
      data.owner_reason = '';
    } else {
      data.owner_employee = null;
    }
    const next = [...vehicles];
    if (editIdx !== null) next[editIdx] = data;
    else next.push(data);
    setVehicles(next);
    setFormOpen(false);
    saveToBackend(next);
  };

  return {
    vehicles,
    setVehicles,
    companyVehicles,
    allVehiclesForClaim,
    otherCompanyVehicles,
    saved,
    saveError,
    listOpen,
    setListOpen,
    formOpen,
    setFormOpen,
    vehicleForm,
    setVehicleForm,
    editIdx,
    setEditIdx,
    fetchCompanyVehicles,
    handleSetDefault,
    handleDeleteVehicle,
    openAdd,
    openEdit,
    handleFormSave,
  };
}
